import { defineStore } from "pinia";
import { ref } from "vue";
import type { RouteRecordNormalized } from "vue-router";
import type { CustomComposerTranslationKey } from "~/types";
import { useCurrentPlant } from "./plant-state";

/* These keys are used in definePageMeta on pages. If you change them here,
 * you'll need to update the pages in which they're used and tests.
 */
export declare type SUB_MENU_NAME =
  | "general-reports"
  | "inspection-reports"
  | "labor-reports"
  | "training-reports"
  | "employees"
  | "equipment-area-information"
  | "inspections";
export declare type MENU_NAME =
  | "main"
  | "home"
  | "KLEANZConnect"
  | "dashboard"
  | "tutorials"
  | "correctiveActions"
  | "generateWork"
  | "laborHistoryEntry"
  | "reporting"
  | "settings"
  | "createTasks"
  | "assignWork";

export declare type CurrentMenu = MENU_NAME | null;

export declare type MainMenuNavigation = CollapsableMenu[];

export declare type CollapsableMenu = {
  key: MENU_NAME;
  label: CustomComposerTranslationKey;
  icon: string;
  order: number;
} & (
  | { external: true; subMenu?: false; url?: "" | null }
  | { external?: false; subMenu: true; url?: "" | null }
  | RestrictedAccess<{ external?: boolean; subMenu?: boolean; url?: string }>
);

export declare type SubMenuNavigation = Record<string, null | Menu | MenuWithSubMenus>;

export declare type Menu = {
  heading: CustomComposerTranslationKey;
  links: LinkData[];
};

export declare type SubMenu = {
  heading: CustomComposerTranslationKey;
  key: SUB_MENU_NAME;
  order: number;
  links: LinkData[];
};

export declare type MenuWithSubMenus = {
  heading: CustomComposerTranslationKey;
  subMenus: (SubMenu | LinkData)[];
};

export declare type LinkData = RestrictedAccess<{
  subMenu?: SUB_MENU_NAME;
  key: string;
  url: string;
  text: CustomComposerTranslationKey;
  order: number;
  external?: boolean;
}>;

export declare type NavLinkData = RestrictedAccess<{
  subMenu?: SUB_MENU_NAME;
  key: string;
  text: CustomComposerTranslationKey;
  order: number;
}>;

export declare type NavSubMenu = {
  heading: CustomComposerTranslationKey;
  key: SUB_MENU_NAME;
  order: number;
};

export declare type NavCollapsableMenu = {
  key: MENU_NAME;
  label: CustomComposerTranslationKey;
  icon: string;
  order: number;
} & (
  | { external?: false; subMenu: true; url?: "" | null; hasSubMenus?: boolean }
  | { external: true; subMenu?: false; url?: "" | null; hasSubMenus?: never }
  | RestrictedAccess<{ external?: boolean; subMenu?: false; url?: string; hasSubMenus?: never }>
);

declare module "#app" {
  interface PageMeta {
    menu?: MENU_NAME;
    nav?:
      | {
          rootMenu?: NavCollapsableMenu;
        }
      | ({
          rootMenu?: NavCollapsableMenu;
        } & (
          | {
              subMenu: NavSubMenu;
            }
          | {
              link: NavLinkData;
            }
        ));
  }
}

declare module "vue-router" {
  interface RouteMeta {
    menu?: MENU_NAME;
    nav?:
      | {
          rootMenu?: NavCollapsableMenu;
        }
      | ({
          rootMenu?: NavCollapsableMenu;
        } & (
          | {
              subMenu: NavSubMenu;
            }
          | {
              link: NavLinkData;
            }
        ));
  }
}

const DEFAULT_LOCALE = "en";

type OrderedObject = { order?: number };
function sortByOrder(a: OrderedObject, b: OrderedObject) {
  return Math.sign((a.order ?? 0) - (b.order ?? 0));
}

type NamespacedSubMenuKey<
  T_Menu extends MENU_NAME = MENU_NAME,
  T_Sub extends SUB_MENU_NAME = SUB_MENU_NAME,
> = `${T_Menu}:${T_Sub}`;
function namespacedMenuKey<T_Menu extends MENU_NAME = MENU_NAME, T_Sub extends SUB_MENU_NAME = SUB_MENU_NAME>(
  menu: T_Menu,
  subMenu: T_Sub,
): NamespacedSubMenuKey<T_Menu, T_Sub> {
  return `${menu}:${subMenu}`;
}

/* Instantiate nav menu data and related methods */
export const useNavigationStore = defineStore("navigation", () => {
  const localePath = useLocalePath();
  const currentPlant = useCurrentPlant();
  const authStore = useAuthStore();
  const router = useRouter();
  const config = useRuntimeConfig();
  const CompanyInfo = useCompanyInfoStore();

  const { isMultiTenant } = useIsMultiTenant();

  CompanyInfo.isReady().then($ck.utils.DO_NOTHING);

  const localeRoutes = computed<RouteRecordNormalized[]>(() =>
    router.getRoutes().filter((r) => (typeof r.name === "string" ? r.name.endsWith("__en") : false)),
  );

  /* use page metadata (from definePageMeta) to generate sub menu links */
  const navBarSubMenuLinks = computed<Record<NamespacedSubMenuKey, LinkData[]>>(() =>
    localeRoutes.value.reduce(
      (acc, el) => {
        if (!el.meta.menu || !el.meta.nav || !("link" in el.meta.nav)) return acc;
        if ("subMenu" in el.meta.nav.link) {
          const { subMenu, ...link } = el.meta.nav.link as { subMenu: SUB_MENU_NAME } & NavLinkData;
          const menuKey = namespacedMenuKey(el.meta.menu, subMenu);
          if (!(menuKey in acc)) {
            acc[menuKey] = [] as LinkData[];
          }
          acc[menuKey].push({ ...link, url: el.path });
          acc[menuKey].sort(sortByOrder);
        }
        return acc;
      },
      {} as Record<NamespacedSubMenuKey, LinkData[]>,
    ),
  );

  /* If a sub menu has sections with headings (like the settings menu as of 2/20/26), assemble sections */
  const subMenuData = computed(() => {
    const subNav = localeRoutes.value.reduce(
      (acc: Record<MENU_NAME, Menu | MenuWithSubMenus | null>, el) => {
        if (el.meta?.nav?.rootMenu) {
          const menu = el.meta.nav.rootMenu;
          let rootMenu: Menu | MenuWithSubMenus = { heading: menu.label, links: [] };
          if (menu.subMenu && menu.hasSubMenus) {
            rootMenu = { heading: menu.label, subMenus: [] };
          }
          acc[menu.key] = rootMenu;
        }
        return acc;
      },
      {} as Record<MENU_NAME, Menu | MenuWithSubMenus | null>,
    );
    localeRoutes.value.forEach((el) => {
      if (el.meta.menu && el.meta.nav) {
        let linkOrSubMenu: undefined | LinkData | SubMenu;
        if ("link" in el.meta.nav) {
          linkOrSubMenu = { ...el.meta.nav.link, url: el.path };
          if (linkOrSubMenu?.subMenu) return;
        }
        if ("subMenu" in el.meta.nav) {
          linkOrSubMenu = {
            ...el.meta.nav.subMenu,
            links: navBarSubMenuLinks.value[namespacedMenuKey(el.meta.menu, el.meta.nav.subMenu.key)],
          };
        }

        if (!linkOrSubMenu) return;

        const subMenu = subNav[el.meta.menu];
        if (subMenu) {
          const storedArray =
            "subMenus" in subMenu && Array.isArray(subMenu?.subMenus) ? subMenu.subMenus : (subMenu as Menu).links;
          storedArray.push(linkOrSubMenu);
          storedArray.sort(sortByOrder);
        }
      }
    });

    // Extra links for menu items
    baseSubMenuLinks.value.forEach(({ menu, ...linkData }) => {
      const subMenu = subNav[menu];
      if (subMenu) {
        const storedArray =
          "subMenus" in subMenu && Array.isArray(subMenu?.subMenus) ? subMenu.subMenus : (subMenu as Menu).links;
        storedArray.push(linkData);
        storedArray.sort(sortByOrder);
      }
    });

    return subNav;
  });

  /* add external links to menu */
  const baseMenuItems: CollapsableMenu[] = [
    {
      key: "KLEANZConnect",
      label: "sideMenu.kleanzConnect",
      external: true,
      icon: "",
      order: 20,
    },
    {
      key: "dashboard",
      label: "sideMenu.dashboard",
      icon: "fa6-solid:chart-line",
      external: true,
      order: 30,
      hide: computed(() => {
        return CompanyInfo.resolved && !!CompanyInfo.frontendFlags.HIDE_SIDEBAR_DASHBOARD_LINK;
      }),
    },
    {
      key: "tutorials",
      label: "sideMenu.tutorials",
      icon: "fluent:play-32-regular",
      external: true,
      order: 1010,
      hide: computed(() => {
        return CompanyInfo.resolved && !!CompanyInfo.frontendFlags.HIDE_SIDEBAR_TUTORIAL_LINK;
      }),
    },
  ] as const;

  /* If using multi-tenant, add external link for multi-tenant to settings submenu. */
  const baseSubMenuLinks = computed<(LinkData & { menu: MENU_NAME })[]>(() =>
    $ck._.compact([
      isMultiTenant.value && config.public.APP_URL_SETUP && currentPlant.data?.plantId
        ? {
            menu: "settings",
            key: "self-service",
            text: "sideMenu.selfService",
            order: 60,
            external: true,
            url: new URL(`setup/${currentPlant.data?.plantId}`, config.public.APP_URL_SETUP).toString(),
          }
        : undefined,
    ]),
  );

  /* Put it all together and you have your menu data */
  const mainMenuNavigation = computed(() =>
    [
      ...localeRoutes.value.reduce((acc, el) => {
        if (el.meta?.nav?.rootMenu) {
          const { hasSubMenus, ...menuData } = el.meta.nav.rootMenu;
          const menu = { ...menuData, url: el.path } as CollapsableMenu;
          acc.push(menu);
        }
        return acc;
      }, [] as CollapsableMenu[]),
      ...baseMenuItems,
    ].toSorted(sortByOrder),
  );

  const hasAccessTo = (item: MaybeRestrictedAccess) =>
    !hideSidebarItem(item) && hasUserAccess(item, authStore.currentAccount as UserAuthorization);

  const rawHierarchy = computed(() => getNavigationHierarchy(mainMenuNavigation.value, subMenuData.value));

  const authorizedHierarchy = computed(() => filterNavHierarchy(rawHierarchy.value, hasAccessTo));

  const navigationRouteHash = computed(
    () =>
      new Map(
        compactMap(flattenHierarchy(rawHierarchy.value), (n) =>
          "url" in n && n.url?.length ? [localePath(n.url, DEFAULT_LOCALE), n] : undefined,
        ),
      ),
  );

  const matchMenuItemByPath = (path: string) => {
    const match = navigationRouteHash.value.get(localePath(path, DEFAULT_LOCALE));
    if (!match) return;

    return {
      hasAccess: hasAccessTo(match),
      match,
    };
  };

  const currentMenu = ref<MENU_NAME>(router.currentRoute.value.meta.menu || "main");
  const updateCurrentMenu = (val: MENU_NAME) => {
    currentMenu.value = val;
  };

  return {
    rawHierarchy,
    authorizedHierarchy,
    matchMenuItemByPath,
    currentMenu: readonly(currentMenu),
    updateCurrentMenu,
    subMenuData,
    localeRoutes,
    mainMenuNavigation,
    navBarSubMenuLinks,
  };
});
